import { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';

import { useGameConfig } from '../../config/GameConfigProvider';
import { useTotalBetAfterSubscription } from '../../hooks/useTotalBetAfterSubscription';
import { useCoreDispatch } from '../../store/CoreStore';
import { useBonusPromotionsSlice } from '../../store/slices/bonusPromotions/selectors';
import { useChipSlice } from '../../store/slices/chips/selectors';
import { ChipTypes } from '../../store/slices/chips/types';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useInitializationSlice } from '../../store/slices/initialization/selectors';
import { toastActions } from '../../store/slices/toast/slice';

/**
 * This message appears every time a player opens/refreshes a table where the selected chip is higher than the minimum bet available for the table. 
 * If the default chip is equal or lower than the min bet or is free chip - we do not show the tooltip
 * The chip shown in the toast notification is the one equal to table min bet
 * The tooltip gets removed once -
    - Player manually closes it with X action
    - Player decreases the chip by selecting a chip equal or lower than the min bet
    - Player places a bet with any chip
 */

enum ToastDisplay {
  NotShown,
  Shown,
  Closed,
  Skipped,
}
export const JurisdictionNL: FC = (): ReactNode => {
  const [toast, setToast] = useState<ToastDisplay>(ToastDisplay.NotShown);

  const dispatch = useCoreDispatch();
  const tableConfig = useConfigSlice.tableConfig();
  const isCrashGame = useGameConfig().config.isCrashGame;
  const isBetPlaced = useTotalBetAfterSubscription();
  const playerConfig = useConfigSlice.playerConfig();
  const casinoConfig = useConfigSlice.casinoConfig();
  const selectedChip = useChipSlice.getSelectedChip();
  const isReadyToRender = useInitializationSlice.getIsReadyToRender();
  const isFreeChipSelected = selectedChip?.type === ChipTypes.FreeChip;
  const isBonusInitialised = useBonusPromotionsSlice.getIsBonusPromotionsInitialised();

  useEffect(() => {
    if (toast === ToastDisplay.Skipped || toast === ToastDisplay.Closed) return;
    if (!isReadyToRender || !isBonusInitialised) return;

    if (playerConfig?.jurisdiction !== 'NL') return;
    if (!casinoConfig?.showNlMinBetToast) return;

    const tableLimits = tableConfig?.tableBetLimit;
    if (!tableLimits) return;
    const defaultBetAmount = tableConfig?.defaultBetAmount;
    const betingAmountValue = isCrashGame ? defaultBetAmount : selectedChip?.value;
    if (!betingAmountValue) return;

    if (toast === ToastDisplay.NotShown) {
      if (isFreeChipSelected) {
        setToast(ToastDisplay.Skipped);
      } else if (betingAmountValue > tableLimits.min) {
        setToast(ToastDisplay.Shown);
        dispatch(toastActions.createToast('TOAST_BET_ABOVE_MINIMUM'));
      } else {
        setToast(ToastDisplay.Skipped);
      }
    }

    if (toast === ToastDisplay.Shown) {
      if (betingAmountValue <= tableLimits.min || isBetPlaced || isFreeChipSelected) {
        setToast(ToastDisplay.Closed);
        dispatch(toastActions.removeNLToast());
      }
    }
  }, [
    tableConfig,
    casinoConfig,
    dispatch,
    isBetPlaced,
    isBonusInitialised,
    isCrashGame,
    isFreeChipSelected,
    isReadyToRender,
    playerConfig,
    selectedChip,
    toast,
  ]);

  return null;
};
